import { createRef, useCallback, useLayoutEffect, useState } from 'react';

import _ from 'lodash';

/**
 * @typedef {object} ScrollCallBacks
 * @property {object} scrollContainerRef - ref setter for scrollable container
 * @property {function} triggerScrollIntoView - callback for running scroll with options
 * (options from scrollIntoView DOM API,
 * applyToElement - path to node for applying scroll,
 * querySelector - a query string to any element inside container
 *
 * @example
 *
 * // Make sure that the value of the attribute inside query selector is a string, not a number
 * triggerScrollIntoView({ querySelector: `[data-scrollid="${id}"]`})
 *
 * )
 */

/**
 * Hook for calling scrollIntoView on ref element
 * @param {object} [ref] - scrollIntoView will applied to passed ref (optional).
 * @returns ScrollCallBacks
 */

const useScrollIntoView = ref => {
  const scrollContainerRef = ref || createRef();
  const [scrollOptions, setScrollOptions] = useState(null);

  const triggerScrollIntoView = useCallback((scrollOptions = {}) => {
    setScrollOptions(scrollOptions);
  }, []);

  useLayoutEffect(() => {
    if (scrollOptions) {
      const { applyToElement, querySelector, ...restOptions } = scrollOptions;
      const elementToScroll = querySelector
        ? scrollContainerRef.current?.querySelector(querySelector)
        : _.get(scrollContainerRef.current, applyToElement, scrollContainerRef.current);

      elementToScroll?.scrollIntoView?.(restOptions);
      setScrollOptions(null);
    }
  }, [scrollOptions, scrollContainerRef, setScrollOptions]);

  return { scrollContainerRef, triggerScrollIntoView };
};

export default useScrollIntoView;
